import { useEffect, useMemo, useState, useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import planApi from '@api/planApi';
import { normalizePage } from '@utils/paging';
import { AuthContext } from '@context';

function safeRows(data) {
  if (!data) return [];
  if (Array.isArray(data.list)) return data.list;
  if (Array.isArray(data.rows)) return data.rows;
  return [];
}

export default function usePlanAssignPage() {
  const queryClient = useQueryClient();
  const { auth } = useContext(AuthContext);

  const [filters, setFilters] = useState({ 
    branch: '', jurisdiction: '', assignee: '', companyName: '', visitRound: '', worksiteName: '', bizRegNo: '', pageNum: 1, pageSize: 10 
  });
  
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingRows, setEditingRows] = useState({});
  const [dirtyRowIds, setDirtyRowIds] = useState({});
  const [savingRowIds, setSavingRowIds] = useState({});
  
  const [isExcelOpen, setIsExcelOpen] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [isRegistModalOpen, setIsRegistModalOpen] = useState(false); 
  const [serverMessage, setServerMessage] = useState('');

  const [staffList, setStaffList] = useState([]);

  // 🌟 (추가) 행 아코디언 열림/닫힘 상태
  const [expandedRows, setExpandedRows] = useState({});
  
  const toggleExpand = (bizMgtNo) => {
    setExpandedRows(prev => ({ ...prev, [bizMgtNo]: !prev[bizMgtNo] }));
  };

  useEffect(() => {
    planApi.getAllStaffForMember().then(res => setStaffList(res || [])).catch(() => {});
  }, []);

  const query = useQuery({
    queryKey: ['plan', 'assignments', filters],
    queryFn: () => planApi.listAssignments(filters),
    keepPreviousData: true,
  });

  const bizListQuery = useQuery({
    queryKey: ['plan', 'bizSelect'],
    queryFn: () => planApi.listBizForSelect()
  });

  const page = useMemo(() => {
    const n = normalizePage(query.data);
    if ((!n || !n.rows || n.rows.length === 0) && query.data) {
      return { 
        rows: safeRows(query.data), 
        total: query.data.total || 0, 
        pageNum: query.data.pageNum || filters.pageNum, 
        pageSize: query.data.pageSize || filters.pageSize 
      };
    }
    return n || { rows: [], total: 0, pageNum: filters.pageNum, pageSize: filters.pageSize };
  }, [query.data, filters.pageNum, filters.pageSize]);

  useEffect(() => {
    setEditingRows({}); 
    setDirtyRowIds({}); 
    setSavingRowIds({}); 
    setSelectedIds([]);
  }, [page.rows]);

  // 🌟 (핵심) 데이터가 변경되었을 때 연관된 모든 캐시를 날려버리는 통합 함수
  const invalidateRelatedQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['plan', 'visits'] });
    // 방문 데이터가 바뀌면 산출물 관리 페이지의 데이터도 바뀌어야 하므로 같이 날려줍니다.
    queryClient.invalidateQueries({ queryKey: ['plan', 'deliverables'] }); 
    queryClient.invalidateQueries({ queryKey: ['plan', 'assignments'] });
    queryClient.invalidateQueries({ queryKey: ['plan', 'progress'] });
    queryClient.invalidateQueries({ queryKey: ['plan', 'dashboard'] });
  };

  // 🌟 (추가) 통합 담당자 1 저장 Mutation
  const patchCompanyStaff1Mutation = useMutation({
    mutationFn: ({ bizMgtNo, staffId1 }) => planApi.patchCompanyStaff1(bizMgtNo, staffId1),
    onSuccess: invalidateRelatedQueries
  });

  const patchRowMutation = useMutation({
    mutationFn: ({ assignNo, payload }) => planApi.patchAssignmentAssignees(assignNo, payload),
    onSuccess: invalidateRelatedQueries
  });
  
  const excelUploadMutation = useMutation({
    mutationFn: (file) => planApi.uploadAssignmentExcel(file),
    onSuccess: invalidateRelatedQueries
  });

  const registAssignmentMutation = useMutation({
    mutationFn: (payload) => planApi.upsertAssignment(payload), 
    onSuccess: () => {
      alert("배정 정보가 성공적으로 등록되었습니다.");
      setIsRegistModalOpen(false);
      invalidateRelatedQueries();
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.message || error.message || "배정 등록 중 오류가 발생했습니다.";
      //alert(errorMsg);
    }
  });

  const deleteAssignmentsMutation = useMutation({
    mutationFn: (assignNos) => planApi.deleteAssignments(assignNos),
    onSuccess: () => {
      alert("선택한 항목이 삭제되었습니다.");
      setSelectedIds([]);
      invalidateRelatedQueries();
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.message || error.message || "배정 등록 중 오류가 발생했습니다.";
      //alert(errorMsg);
    }
  });

  const handleAddRound = async (group) => {
    // 1. 방어 로직: 통합 담당자가 배정되어 있어야 함
    if (!group.staffId1) {
      alert("먼저 해당 사업장의 '방문 담당자 1 (통합)'을 배정해주세요.");
      return;
    }

    // 2. 다음 회차 계산 (하위 목록이 없으면 1회차, 있으면 MAX + 1)
    const nextRound = group.rounds && group.rounds.length > 0 
      ? Math.max(...group.rounds.map(r => r.visitRound)) + 1 
      : 1;

    if (nextRound > 5) {
      alert("방문 일정은 최대 5회차까지만 배정할 수 있습니다.");
      return;
    }

    // 3. 사용자 확인 후 기존 regist API(upsert) 호출
    if (window.confirm(`${group.companyName} 사업장에 [${nextRound}회차] 방문 일정을 추가하시겠습니까?`)) {
      try {
        await registAssignmentMutation.mutateAsync({
          bizMgtNo: group.bizMgtNo,
          staffId1: group.staffId1,
          visitRound: nextRound,
          assignStatus: "방문예정" // 🌟 상태 고정
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  const getKey = (row) => row.bizMgtNo ?? row.assignmentId ?? row.assignNo;

  const startEdit = (row, editType, isGroup = false) => {
    // 그룹(회사) 편집이면 bizMgtNo를, 개별 회차면 assignNo를 ID로 사용
    const id = isGroup ? row.bizMgtNo : (row.assignNo ?? row.assignmentId);
    setEditingRows(prev => ({ 
      ...prev, 
      [id]: {
        staffId1: row.staffId1 || '', 
        staffId2: row.staffId2 || '', 
        editType: editType 
      }
    }));
  };
  // 🌟 (추가) 편집 취소 핸들러
  const cancelEdit = (rowId) => {
    setEditingRows(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setDirtyRowIds(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const updateEditRow = (rowId, patch) => {
    setEditingRows(prev => ({ ...prev, [rowId]: { ...(prev[rowId] || {}), ...patch } }));
    setDirtyRowIds(prev => ({ ...prev, [rowId]: true }));
  };

  const saveRow = async (row, isGroup = false) => {
    const id = isGroup ? row.bizMgtNo : (row.assignNo ?? row.assignmentId);
    const edited = editingRows[id];
    
    if (!edited) return;

    try {
      setSavingRowIds(p => ({ ...p, [id]: true }));
      
      if (isGroup) {
        // 그룹: 담당자 1 일괄 변경
        if (!String(edited.staffId1 || '').trim()) return alert('통합 방문담당자 1은 필수입니다.');
        await patchCompanyStaff1Mutation.mutateAsync({ bizMgtNo: id, staffId1: edited.staffId1 });
      } else {
        // 하위: 담당자 2 등 개별 변경
        await patchRowMutation.mutateAsync({ assignNo: id, payload: edited });
      }
      
      cancelEdit(id);
    } catch (e) {
      alert(e.message); 
    } finally {
      setSavingRowIds(p => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  const toggleSelectRow = (rowId, checked) => {
    setSelectedIds(prev => checked ? Array.from(new Set([...prev, rowId])) : prev.filter(id => id !== rowId));
  };
  const toggleSelectAllCurrentPage = (checked) => {
    const ids = (page.rows || []).map(getKey);
    setSelectedIds(prev => checked ? Array.from(new Set([...prev, ...ids])) : prev.filter(id => !ids.includes(id)));
  };
  
  const allCheckedOnPage = (page.rows || []).length > 0 && (page.rows || []).every(r => selectedIds.includes(getKey(r)));
  const anyChecked = selectedIds.length > 0;

  const handleRegistClick = () => {
    setIsRegistModalOpen(true);
  };

  const handleDeleteClick = async () => {
    if (selectedIds.length === 0) {
      alert("삭제할 항목을 먼저 선택해주세요.");
      return;
    }
    if (window.confirm(`선택한 ${selectedIds.length}개 항목을 삭제하시겠습니까?\n(삭제 후 복구할 수 없습니다.)`)) {
      try {
        await deleteAssignmentsMutation.mutateAsync(selectedIds);
      } catch (error) {
        const message= error.message;
        alert("삭제 중 오류가 발생했습니다.");
      }
    }
  };

  const handleExcelUpload = async (file) => {
    if (!file) return;
    try {
      const res = await excelUploadMutation.mutateAsync(file);
      alert(res?.success ? '업로드 완료' : '업로드 실패');
      if (res?.success) setIsExcelOpen(false); 
    } catch (e) { alert(e?.message || '오류 발생'); }
  };

  const downloadExcel = async () => {
    setIsDownloadingExcel(true);
    try {
      const res = await planApi.downloadAssignmentsExcel(filters);
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '배정목록_다운로드.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      alert("엑셀 다운로드 중 오류가 발생했습니다.");
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const onSearch = () => { 
    setFilters(p => ({ ...p, pageNum: 1 })); 
    setSelectedIds([]); 
  };

  return {
    filters, setFilters, query, page, serverMessage,
    selectedIds, toggleSelectRow, toggleSelectAllCurrentPage, allCheckedOnPage, anyChecked,
    editingRows, expandedRows, toggleExpand, startEdit, cancelEdit, updateEditRow, getKey, saveRow, // 🌟 훅 반환값 수정
    handleAddRound,
    isRegistModalOpen, setIsRegistModalOpen, handleRegistClick,
    registAssignment: registAssignmentMutation.mutateAsync,
    isRegisting: registAssignmentMutation.isPending,
    handleDeleteClick, isDeleting: deleteAssignmentsMutation.isPending,

    isExcelOpen, setIsExcelOpen, handleExcelUpload, isUploading: excelUploadMutation.isPending,
    onSearch, downloadExcel, isDownloadingExcel,
    staffList, auth, bizList: bizListQuery?.data || [],
  };
}